/**
 * SlotDetailController — 슬롯 상세 모달 Stimulus 컨트롤러
 *
 * Turbo Frame "slot-detail-modal" 내부에서 동작하며
 * 닉네임 인라인 편집, 이미지 미리보기, 모달 닫기를 관리한다.
 *
 * 타겟:
 *   nicknameDisplay — 닉네임 읽기모드 영역
 *   nicknameForm    — 닉네임 편집모드 영역 (기본 hidden)
 *   imagePreview    — 프로필 이미지 미리보기 (img 또는 div)
 *   imageInput      — 파일 선택 input[type="file"]
 *
 * 액션:
 *   startEdit()       — 읽기모드 → 편집모드 전환
 *   cancelEdit()      — 편집모드 → 읽기모드 복귀
 *   previewImage(e)   — 파일 선택 시 FileReader로 이미지 미리보기
 *   closeModal()      — Turbo Frame 초기화 (모달 닫기)
 *   stopPropagation(e)— 모달 박스 클릭 시 오버레이 닫기 방지
 *   inputFocus(e)     — 입력 필드 포커스 시 보더 강조
 *   inputBlur(e)      — 입력 필드 블러 시 보더 복원
 *
 * 사용법:
 *   data-controller="slot-detail"
 *   data-action="click->slot-detail#startEdit"
 *   data-slot-detail-target="nicknameDisplay"
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // 타겟 정의 — 닉네임 표시/편집 영역, 이미지 미리보기/파일 입력
  static targets = [
    "nicknameDisplay",  // 닉네임 읽기모드 영역
    "nicknameForm",     // 닉네임 편집모드 영역
    "imagePreview",     // 프로필 이미지 미리보기 요소
    "imageInput"        // 파일 선택 input
  ]

  // ── 초기화 ──────────────────────────────────────────────
  connect() {
    // 키보드 ESC 이벤트 처리를 위해 포커스 설정
    this.element.setAttribute("tabindex", "-1")
    this.element.focus()
  }

  // ── 닉네임 편집 시작 ──────────────────────────────────
  // 읽기모드를 숨기고 편집 폼을 표시한다
  startEdit() {
    this.nicknameDisplayTarget.classList.add("hidden")
    this.nicknameFormTarget.classList.remove("hidden")

    // 편집 폼 내 입력 필드에 자동 포커스
    const input = this.nicknameFormTarget.querySelector("input[type='text']")
    if (input) {
      input.focus()
      input.select()
    }
  }

  // ── 닉네임 편집 취소 ──────────────────────────────────
  // 편집 폼을 숨기고 읽기모드를 복원한다
  cancelEdit() {
    this.nicknameFormTarget.classList.add("hidden")
    this.nicknameDisplayTarget.classList.remove("hidden")
  }

  // ── 이미지 미리보기 ──────────────────────────────────
  // input[file] change 이벤트 → FileReader → 이미지 즉시 반영
  previewImage(event) {
    const file = event.target.files[0]
    if (!file) return

    // 이미지 파일만 허용
    if (!file.type.startsWith("image/")) return

    const form = event.target.closest("form")

    const reader = new FileReader()
    reader.onload = (e) => {
      const preview = this.imagePreviewTarget

      // img 태그인 경우 src 업데이트
      if (preview.tagName === "IMG") {
        preview.src = e.target.result
      } else {
        // div 컨테이너인 경우 (마스코트 폴백) → img 태그로 교체
        const img = document.createElement("img")
        img.src = e.target.result
        img.alt = "미리보기"
        img.className = "w-full h-full object-cover"
        img.style.cssText = "border: 2px solid var(--neon-primary-30); border-radius: 2px; filter: saturate(0.9) contrast(1.1);"
        img.setAttribute("data-slot-detail-target", "imagePreview")
        preview.replaceWith(img)
      }

      if (form) {
        form.requestSubmit()
      }
    }
    reader.readAsDataURL(file)
  }

  // ── 모달 닫기 ────────────────────────────────────────
  // Turbo Frame 내용을 비워 모달을 제거한다
  closeModal() {
    const frame = document.querySelector("turbo-frame#slot-detail-modal")
    if (frame) {
      // Turbo Frame 내부 HTML 초기화 → 모달 DOM 제거
      frame.innerHTML = ""
      frame.removeAttribute("src")
    }
  }

  // ── 이벤트 전파 중단 ─────────────────────────────────
  // 모달 박스 클릭 시 오버레이 닫기가 트리거되지 않도록 방지
  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── 입력 필드 포커스 강조 ─────────────────────────────
  // 사이버펑크 네온 그린 보더 강조 효과
  inputFocus(event) {
    event.target.style.borderColor = "var(--neon-primary)"
    event.target.style.boxShadow = "0 0 12px var(--neon-primary-15)"
  }

  // ── 입력 필드 블러 복원 ───────────────────────────────
  // 기본 보더 색상으로 복원
  inputBlur(event) {
    event.target.style.borderColor = "var(--neon-primary-30)"
    event.target.style.boxShadow = "none"
  }
}
